/**
 *
 * Cart
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from '@artemis/integrations/contentful/utils';
import ItemPrice from './ItemPrice';
import { CartItemShape } from './propTypes';
import ItemDetails from './ItemDetails';

const Container = styled.ul`
  overflow-y: hidden;
  list-style: none;
  margin: 0;
  padding: 0;
  & > :not(:last-child) {
    border-bottom: none;
  }
`;

const Item = styled.div`
  display: grid;
  grid-template-areas:
    'quantity details price'
    'quantity remove  price';
  grid-template-columns: auto 1fr auto;
  column-gap: 16px;
  justify-content: space-between;
  padding: 16px 0;
  border-top: 1px solid ${props => props.theme.palette.grey[100]};
  border-bottom: 1px solid ${props => props.theme.palette.grey[100]};
  ${props => props.shouldModify && 'cursor: pointer'};
`;

const Quantity = styled.div`
  grid-area: quantity;
  display: flex;
  justify-content: center;
  align-items: center;
  ${props => props.theme.typography.bodySmall};
  font-weight: ${props => props.theme.typography.fontWeightMedium};
  width: 24px;
  height: 24px;
  background: ${props => props.theme.palette.grey[100]};
  border-radius: 4pt;
`;

const StyledItemDetails = styled(ItemDetails)`
  grid-area: details;
`;

const StyledItemPrice = styled(ItemPrice)`
  grid-area: price;
`;

const RemoveFromCart = styled.button`
  grid-area: remove;
  color: ${props => props.theme.palette.primary};
  width: fit-content;
  background-color: transparent;
  border: none;
  padding: 0px;
  ${props => props.theme.typography.bodySmall};
  font-weight: ${props => props.theme.typography.fontWeightMedium};
  transition: color 0.25s ease;

  &:hover {
    color: ${({ theme }) =>
      theme.palette.hover.onLightBg(theme.palette.primary)};
    text-decoration: underline;
  }
`;

const Cart = ({
  items = [],
  onModifyItem,
  onRemoveFromCart,
  currencyCode,
  className,
}) => {
  const shouldModify = !!onModifyItem;
  return (
    <Container className={className}>
      {items.map(item => (
        <li key={`${item.cartItemId}`}>
          <Item
            role="group"
            aria-label="Product"
            shouldModify={shouldModify}
            onClick={() =>
              shouldModify &&
              onModifyItem({
                cartItemId: item.cartItemId,
                itemId: item.menuItemId,
                options: item.cartItemOptions,
                quantity: item.quantity,
                note: item.note,
                perk: item.perk,
              })
            }
          >
            <Quantity>{item.quantity}</Quantity>
            <StyledItemDetails
              item={item}
              onRemoveFromCart={onRemoveFromCart}
              currencyCode={currencyCode}
            />
            <StyledItemPrice price={item.price} perk={item.perk || null} />
            {onRemoveFromCart && (
              <RemoveFromCart
                data-testid={`remove-${item.cartItemId}`}
                onClick={e => {
                  onRemoveFromCart({
                    item: item.cartItemId,
                  });
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <FormattedMessage entry="cart.remove" />
              </RemoveFromCart>
            )}
          </Item>
        </li>
      ))}
    </Container>
  );
};

Cart.propTypes = {
  items: PropTypes.arrayOf(CartItemShape),
  onModifyItem: PropTypes.func,
  onRemoveFromCart: PropTypes.func,
  currencyCode: PropTypes.string.isRequired,
  className: PropTypes.string,
};

export default Cart;
